import { useEffect, useState } from 'react';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import ChangePasswordDialog from '../components/ChangePasswordDialog';
import ConnectedCalendars from '../components/ConnectedCalendars';
import { Lock, Bell, Download, User as UserIcon, Link as LinkIcon } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { ROLE_LABELS } from '../lib/utils';
import { toast } from 'sonner';

const PREF_GROUPS = [
  {
    key: 'email', label: 'Email notifications',
    items: [
      ['email_lead_assigned', 'New lead / partner assigned to me'],
      ['email_task_assigned', 'New task assigned to me'],
      ['email_partner_closed', 'Partnership closed (Closed-Won)'],
      ['email_followup_reminder', '24-hour follow-up reminder'],
      ['email_daily_digest', 'Daily 8:00 AM digest'],
      ['email_password_reset', 'Password reset alerts'],
    ],
  },
  {
    key: 'in_app', label: 'In-app notifications',
    items: [
      ['in_app_lead_assigned', 'Lead / partner assignments'],
      ['in_app_task_assigned', 'Task assignments'],
      ['in_app_meetings', 'Upcoming meetings'],
      ['in_app_calendar_invites', 'Calendar invitations'],
      ['in_app_document_uploads', 'Document uploads on my leads'],
      ['in_app_account_changes', 'Account & role changes'],
    ],
  },
];

export default function AccountPage() {
  const { user } = useAuth();
  const [prefs, setPrefs] = useState({});
  const [pwdOpen, setPwdOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get('/notification-preferences').then((r) => setPrefs(r.data));
  }, []);

  const toggle = async (key) => {
    const next = { ...prefs, [key]: !prefs[key] };
    setPrefs(next);
    setBusy(true);
    try {
      await api.put('/notification-preferences', next);
    } catch {
      toast.error('Could not save preferences');
    } finally {
      setBusy(false);
    }
  };

  const exportCalendar = async () => {
    const token = localStorage.getItem('aawta_token');
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/calendar/export.ics`;
    try {
      const resp = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!resp.ok) throw new Error('failed');
      const blob = await resp.blob();
      const dlUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = dlUrl;
      a.download = 'aawta-calendar.ics';
      document.body.appendChild(a);
      a.click();
      a.remove();
      toast.success('Calendar (.ics) download started');
    } catch {
      toast.error('Could not download calendar');
    }
  };

  return (
    <div className="max-w-3xl space-y-6" data-testid="account-page">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><UserIcon className="w-5 h-5 text-magenta" /> Profile</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          <div className="flex justify-between border-b pb-2 text-sm"><span className="text-slate-500">Name</span><span className="font-semibold text-navy">{user?.full_name}</span></div>
          <div className="flex justify-between border-b pb-2 text-sm"><span className="text-slate-500">Email</span><span className="font-semibold text-navy">{user?.email}</span></div>
          <div className="flex justify-between border-b pb-2 text-sm"><span className="text-slate-500">Role</span><span className="font-semibold text-navy">{ROLE_LABELS[user?.role]}</span></div>
          <div className="flex justify-between text-sm"><span className="text-slate-500">Title</span><span className="font-semibold text-navy">{user?.title || '—'}</span></div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><Lock className="w-5 h-5 text-magenta" /> Password</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-slate-500 mb-3">Change your password anytime. Passwords must be at least 12 characters with an uppercase letter, lowercase letter, number, and special character.</p>
          <Button onClick={() => setPwdOpen(true)} className="bg-magenta hover:bg-magenta/90" data-testid="account-change-password"><Lock className="w-4 h-4 mr-2" /> Change password</Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><Bell className="w-5 h-5 text-magenta" /> Notification preferences</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {PREF_GROUPS.map((g) => (
            <div key={g.key}>
              <div className="text-[11px] uppercase tracking-wider font-bold text-slate-500 mb-2">{g.label}</div>
              <div className="space-y-2">
                {g.items.map(([k, l]) => (
                  <div key={k} className="flex items-center justify-between bg-slate-50 rounded-lg px-3 py-2">
                    <Label htmlFor={`pref-${k}`} className="text-sm text-navy cursor-pointer">{l}</Label>
                    <Switch id={`pref-${k}`} checked={!!prefs[k]} onCheckedChange={() => toggle(k)} disabled={busy} data-testid={`pref-${k}`} />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      <ConnectedCalendars />

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><LinkIcon className="w-5 h-5 text-magenta" /> Book with Me</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-slate-500 mb-3">Share a public scheduling link so prospects can book directly onto your calendar (new CRM leads are auto-created).</p>
          <a href="/booking" data-testid="account-open-booking">
            <Button className="bg-magenta hover:bg-magenta/90"><LinkIcon className="w-4 h-4 mr-2" /> Manage my booking page</Button>
          </a>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><Download className="w-5 h-5 text-magenta" /> Manual calendar download</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-slate-500 mb-3">Prefer to import manually? Download an .ics file for Google Calendar, Outlook, Apple Calendar, or any calendar app.</p>
          <Button variant="outline" onClick={exportCalendar} data-testid="account-export-ics"><Download className="w-4 h-4 mr-2" /> Download .ics</Button>
        </CardContent>
      </Card>

      <ChangePasswordDialog open={pwdOpen} onOpenChange={setPwdOpen} />
    </div>
  );
}
